// Chat's optimistic writes (CHAT-PERF), on the shared `useOptimisticMutation` (api/optimistic.ts)
// and cache helpers (live/cache-patch.ts). What the viewer does shows at once:
//   - send: the message appears "sending" (faded) in every cached list of its thread; the
//     server's copy replaces it by `client_id` (the API dedupes on it, so Retry cannot post
//     twice); a failure keeps it as "Not sent" with Retry;
//   - react / unreact, pin / unpin, edit, delete: the shown message changes at once and rolls
//     back if the server refuses;
//   - read markers (the topic's and the channel's unread pills drop at once).
// Topic pin / close live in topic-actions.tsx on the same hook. The server's own copy arrives
// either as the mutation's answer or, for everyone else, as a payload ping (live/change-pings.ts).
import { bodyTextOf, markdownToBlocks } from "@companyos/domain-work/chat";
import type { ComposerValue } from "@companyos/ui";
import { type QueryClient, type QueryKey, useQueryClient } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { useOptimisticMutation } from "../../api/optimistic";
import {
  cachedMessage,
  type ListData,
  type LocalMessage,
  messagesPrefix,
  patchLists,
  patchMessage,
  removeRows,
  updateMessage,
} from "../../live/cache-patch";
import { noteSent } from "./asked-here";
import {
  chatKeys,
  clientId,
  latestMessagesQuery,
  type MessageView,
  messagesQuery,
  type SidebarChannel,
  type TopicSummary,
} from "./chat-queries";
import {
  draftClientId,
  draftExtras,
  dropUnsent,
  holdUnsent,
  readDraft,
  refused,
  releaseUnsent,
  requestSig,
  writeDraft,
} from "./chat-switch";
import type { ChatContext } from "./use-chat";

export const MENTION_REF = /^(person|seat|team|de):[0-9a-f-]{36}$/;

type SendCommon = {
  parent?: string | undefined;
  cardRefs?: string[] | undefined;
  /** THREAD-WAKE: the composer's pick (an agent's membership id, or "none" for a note). */
  addressee?: string | undefined;
  /** CHAT-THREAD-AGENTS: a reply also sent to the channel (the agents it wakes copy their answer). */
  alsoToChannel?: boolean | undefined;
  /** CHAT-FILES: staged files sent with the message (the text may then be empty). */
  files?: readonly MessageView["attachments"][number][] | undefined;
};

export type SendBody = ({ value: ComposerValue } | { markdown: string }) & SendCommon;

type MessageCreate = Parameters<Api["client"]["messages"]["create"]>[0]["body"];

export type SendVars = {
  threadId: string;
  channelId: string;
  request: MessageCreate;
  /** The optimistic row (its id is `local-<client_id>` until the server's copy replaces it). */
  local: LocalMessage;
  /** CHAT-SWITCH-A: the draft the text came from, put back if the send fails. */
  rescue?: Rescue | undefined;
};

type Rescue = {
  key: string;
  blocks: unknown[];
  topic: string | null;
  clientId: string;
};

/**
 * CHAT-SWITCH-A: the composer's value marked with the draft it came from (`useDraft`'s key) and
 * the topic it was sent to, so a send that fails after the composer cleared puts both back in
 * that draft, and one that succeeds (or succeeds on Retry) takes them out again.
 */
export function withDraftKey(
  value: ComposerValue,
  key: string | null | undefined,
  topic: string | null = null,
): ComposerValue {
  if (!key) return value;
  return Object.assign({}, value, { draftKey: key, draftTopic: topic });
}

/**
 * Before the request: a copy of the text that survives a reload until the server has it
 * (`useDraft` brings an unacknowledged one back into an empty composer).
 */
export function rescueHeld(
  rescue: Rescue | undefined,
  sent?: { request: MessageCreate; threadId: string; files?: readonly unknown[] },
): void {
  if (rescue)
    holdUnsent(
      rescue.key,
      rescue.clientId,
      rescue.blocks,
      rescue.topic,
      Date.now(),
      sent
        ? {
            request: sent.request as Record<string, unknown> & { client_id: string },
            threadId: sent.threadId,
            ...(sent.files?.length ? { files: [...sent.files] } : {}),
          }
        : undefined,
    );
}

/**
 * A failed send: its held copy stays (never written over a draft that may be mid-save), now
 * free for the composer of its place to bring back, with its topic, the next time it opens empty
 * (`useDraft`); Retry on the message sends it as it is.
 */
export function rescueFailed(rescue: Rescue | undefined, error?: unknown): void {
  // A refusal goes back to its draft; a send that may have reached the server keeps its whole
  // request to be sent again under the same client id (`releaseUnsent`).
  if (rescue) releaseUnsent(rescue.clientId, rescue.key, error === undefined || refused(error));
}

/** A send the server has (at once or on Retry): its copies go, unless edited since. */
export function rescueSent(rescue: Rescue | undefined): void {
  if (!rescue) return;
  dropUnsent(rescue.key, rescue.clientId);
  if (JSON.stringify(readDraft(rescue.key)) === JSON.stringify(rescue.blocks))
    writeDraft(rescue.key, null);
}

export function rescueOf(body: SendBody, clientId: string): Rescue | undefined {
  if (!("value" in body)) return undefined;
  const v = body.value as { draftKey?: unknown; draftTopic?: unknown };
  return typeof v.draftKey === "string" && body.value.blocks.length
    ? {
        key: v.draftKey,
        blocks: body.value.blocks,
        topic: typeof v.draftTopic === "string" && v.draftTopic ? v.draftTopic : null,
        clientId,
      }
    : undefined;
}

/**
 * CHAT-SWITCH-B: the idempotency key of a send — the one its draft keeps from a send the server may
 * already have (brought back after a reload), while it is the same request to the same place
 * (`requestSig`: words, links, mentions, files, addressee…); else a fresh one.
 */
export function sendClientId(body: SendBody, threadId: string): string {
  if (!("value" in body)) return clientId();
  const key = (body.value as { draftKey?: unknown }).draftKey;
  if (typeof key !== "string") return clientId();
  const sig = requestSig(
    threadId,
    withDraftExtras(body, messageRequest(body, "")) as Record<string, unknown>,
  );
  return draftClientId(key, sig) ?? clientId();
}

/**
 * Codex r3 #6: a draft put back from a refused send still carries what that send had besides its
 * text (files, the addressed agent, cards): the request takes each one it does not set itself.
 */
export function withDraftExtras(body: SendBody, request: MessageCreate): MessageCreate {
  if (!("value" in body)) return request;
  const key = (body.value as { draftKey?: unknown }).draftKey;
  const extras = typeof key === "string" ? draftExtras(key) : null;
  if (!extras) return request;
  const r = request as MessageCreate & Record<string, unknown>;
  return {
    ...request,
    ...(extras.attachment_ids && !r.attachment_ids
      ? { attachment_ids: extras.attachment_ids }
      : {}),
    ...(extras.addressee && !r.addressee ? { addressee: extras.addressee } : {}),
    ...(extras.card_refs && !r.card_refs ? { card_refs: extras.card_refs } : {}),
  } as MessageCreate;
}

/**
 * CHAT-SWITCH-A: the @ menu's `@channel` / `@here` entries (use-chat.ts `BROADCAST_MENTIONS`):
 * not member mentions but the message's `broadcast` (`@channel` wins when both are there).
 */
export function broadcastOf(mentions: readonly string[]): "channel" | "here" | undefined {
  if (mentions.includes(BROADCAST_REF.channel)) return "channel";
  if (mentions.includes(BROADCAST_REF.here)) return "here";
  return undefined;
}

export const BROADCAST_REF = { channel: "broadcast:channel", here: "broadcast:here" } as const;

/** The request a composer value or a markdown line makes (`id`: its key, default a fresh one). */
export function messageRequest(body: SendBody, id: string = clientId()): MessageCreate {
  const files = body.files?.map((f) => f.id) ?? [];
  const broadcast = "value" in body ? broadcastOf(body.value.mentions) : undefined;
  const common = {
    client_id: id,
    ...(broadcast ? { broadcast } : {}),
    ...(body.parent ? { parent_message_id: body.parent } : {}),
    ...(body.cardRefs?.length ? { card_refs: body.cardRefs } : {}),
    ...(body.addressee ? { addressee: body.addressee } : {}),
    ...(body.alsoToChannel && body.parent ? { also_to_channel: true } : {}),
    ...(files.length ? { attachment_ids: files } : {}),
  };
  // CHAT-FILES: a files-only message carries no text at all.
  const text = "value" in body ? body.value.text.trim() : body.markdown.trim();
  if (files.length && !text)
    return {
      ...common,
      ...("value" in body
        ? { mentions: body.value.mentions.filter((x) => MENTION_REF.test(x)) }
        : {}),
    };
  return "value" in body
    ? {
        ...common,
        body: body.value.blocks as Record<string, unknown>[],
        mentions: body.value.mentions.filter((x) => MENTION_REF.test(x)),
      }
    : { ...common, body_markdown: body.markdown };
}

/** The row the server will write, as far as the client can tell (CHAT-PERF send). */
export function localMessage(
  chat: Pick<ChatContext, "co" | "meId">,
  threadId: string,
  request: MessageCreate,
  now = new Date(),
  files: readonly MessageView["attachments"][number][] = [],
): LocalMessage {
  const body = (
    "body" in request && request.body
      ? request.body
      : markdownToBlocks(("body_markdown" in request && request.body_markdown) || "")
  ) as MessageView["body"];
  return {
    company_id: chat.co,
    created_at: now.toISOString(),
    id: `local-${request.client_id}`,
    thread_id: threadId,
    author_membership_id: chat.meId,
    author_kind: "person",
    body,
    body_text: bodyTextOf(body as Parameters<typeof bodyTextOf>[0]),
    mentions: ("mentions" in request && request.mentions) || [],
    card_refs: request.card_refs ?? [],
    parent_message_id: request.parent_message_id ?? null,
    client_id: request.client_id,
    edited_at: null,
    deleted_at: null,
    pinned_at: null,
    actor_kind: "person",
    actor_membership_id: chat.meId,
    actor_seat_id: null,
    actor_key_id: null,
    actor_run_id: null,
    actor_acting_for_membership_id: null,
    actor_identity_mode: null,
    actor_crew_agent_id: null,
    actor_policy_snapshot_id: null,
    actor_auth_version: null,
    reactions: [],
    bookmarked: false,
    agent_wakes: [],
    attachments: [...files],
    local: { state: "sending" },
  } as LocalMessage;
}

/** Sends that failed, by their optimistic id: Retry runs the same request again. */
const retries = new Map<string, () => void>();

/** Retry for a "Not sent" message (MessageList `onRetry`); false when it is not one. */
export function retrySend(id: string): boolean {
  const again = retries.get(id);
  if (!again) return false;
  again();
  return true;
}

/**
 * SEND-AUTO-RETRY (2026-10-31): a send that failed for a reason that may pass (offline, a 502 while
 * the API restarts, a timeout) is sent again once by itself after `AUTO_RETRY_MS`, under the same
 * client id (the server dedupes); meanwhile it shows "Not sent" with Retry and its draft is kept.
 * A refusal (4xx) is never retried by itself.
 */
export const AUTO_RETRY_MS = 3_000;
const autoRetried = new Set<string>();

/** True once per optimistic message, for a failure that sending again might get past. */
export function takeAutoRetry(localId: string, error: unknown): boolean {
  if (refused(error) || autoRetried.has(localId)) return false;
  autoRetried.add(localId);
  return true;
}

/** What a sent message changes besides its thread's list: counts, topic order, the room. */
const aroundSend = (co: string, channelId: string, threadId: string): QueryKey[] => [
  // THREAD-WAKE: the post may move its conversation's addressee (the composer's chip).
  chatKeys.agents(co, threadId),
  chatKeys.topics(co, channelId),
  chatKeys.channels(co),
  chatKeys.room(co, channelId),
  // CHAT-CONTROLS: a reply moves its thread to the top of Threads.
  chatKeys.threads(co),
];

/**
 * Optimistic send. `send(threadId, body)` resolves as soon as the message is on screen; the
 * request runs on. `fresh`: the thread was just created (a new topic), so its list is seeded
 * with the message instead of waiting for a first fetch.
 */
export function useSendMessage(chat: ChatContext, channel: { id: string }) {
  const { api, co } = chat;
  const queryClient = useQueryClient();
  const mutation = useOptimisticMutation<SendVars, MessageView>({
    keys: (v) => [messagesPrefix(co, v.threadId)],
    apply: (v) => {
      retries.delete(v.local.id);
      patchMessage(queryClient, co, { ...v.local, local: { state: "sending" } });
    },
    mutationFn: (v) =>
      api.client.messages.create({
        params: { co, id: v.threadId },
        body: v.request,
      }) as Promise<MessageView>,
    reconcile: (m, v) => {
      // Sent after all (a Retry): the text put back in its draft goes again, unless edited since.
      rescueSent(v.rescue);
      // A payload ping may have brought the server's copy (with reactions) first: keep it.
      const shown = cachedMessage(queryClient, co, v.threadId, m.id);
      if (shown && !shown.local) {
        patchLists<LocalMessage>(queryClient, messagesPrefix(co, v.threadId), (d) =>
          removeLocal(d, v.local.id),
        );
        return;
      }
      // `messages.create` answers the row and its agents' wake results (AGENT-MENTION): no
      // reactions yet, not bookmarked.
      const row = m as Partial<MessageView> & Omit<MessageView, "reactions" | "bookmarked">;
      patchMessage(queryClient, co, {
        ...row,
        reactions: row.reactions ?? [],
        bookmarked: row.bookmarked ?? false,
        agent_wakes: row.agent_wakes ?? [],
        attachments: row.attachments ?? [],
      });
    },
    settle: (v) => aroundSend(co, v.channelId, v.threadId),
    onFailure: (error, v) => {
      updateMessage(queryClient, co, v.threadId, v.local.id, (m) => ({
        ...m,
        local: { state: "failed", error: error instanceof Error ? error.message : String(error) },
      }));
      retries.set(v.local.id, () => mutation.mutate(v));
      // Not sent: back in its draft, so a reload keeps it (never over newer typing).
      rescueFailed(v.rescue, error);
      // SEND-AUTO-RETRY: once by itself, unless the viewer pressed Retry first.
      if (takeAutoRetry(v.local.id, error))
        setTimeout(() => {
          retrySend(v.local.id);
        }, AUTO_RETRY_MS);
    },
  });
  return (threadId: string, body: SendBody, o: { fresh?: boolean } = {}): Promise<void> => {
    const request = withDraftExtras(body, messageRequest(body, sendClientId(body, threadId)));
    const local = localMessage(chat, threadId, request, new Date(), body.files ?? []);
    // CHAT-DE: the "isn't in #channel" prompt shows under messages this tab sent.
    noteSent(request.client_id);
    if (o.fresh) seedThread(queryClient, co, threadId);
    return new Promise<void>((resolve) => {
      // `onMutate` patches the cache on the next microtask; the composer clears after that.
      const rescue = rescueOf(body, request.client_id);
      rescueHeld(rescue, { request, threadId, files: body.files ?? [] });
      mutation.mutate({ threadId, channelId: channel.id, request, local, rescue });
      queueMicrotask(resolve);
    });
  };
}

const removeLocal = (d: ListData<LocalMessage>, id: string): ListData<LocalMessage> =>
  removeRows(d, (r) => r.id === id);

/**
 * Empty lists for a thread that was just created (the topic's paged list and the channel
 * stream's page), so its first message shows without waiting for a fetch.
 */
function seedThread(queryClient: QueryClient, co: string, threadId: string) {
  const empty = { data: [], next_cursor: null, prev_cursor: null };
  const paged = messagesQuery({} as Api, co, threadId).queryKey;
  if (queryClient.getQueryData(paged) === undefined)
    queryClient.setQueryData(paged, { pages: [empty], pageParams: [undefined] });
  const latest = latestMessagesQuery({} as Api, co, threadId).queryKey;
  if (queryClient.getQueryData(latest) === undefined) queryClient.setQueryData(latest, empty);
}

type MessageRef = { id: string; threadId: string };

/** React / unreact (the caller's own pill), at once. */
export function useReact(chat: ChatContext) {
  const { api, co, meId } = chat;
  const queryClient = useQueryClient();
  return useOptimisticMutation<MessageRef & { emoji: string; on: boolean }>({
    keys: (v) => [messagesPrefix(co, v.threadId)],
    apply: (v) =>
      updateMessage(queryClient, co, v.threadId, v.id, (m) => ({
        ...m,
        reactions: v.on
          ? [
              ...m.reactions,
              {
                company_id: co,
                message_id: m.id,
                membership_id: meId ?? "",
                emoji: v.emoji,
                created_at: new Date().toISOString(),
              },
            ]
          : m.reactions.filter((r) => !(r.emoji === v.emoji && r.membership_id === meId)),
      })),
    mutationFn: async (v) => {
      const params = { co, id: v.threadId, mid: v.id };
      if (v.on) await api.client.messages.react({ params, body: { emoji: v.emoji } });
      else await api.client.messages.unreact({ params: { ...params, emoji: v.emoji } });
    },
  });
}

/** Merges the server's row of a message (its fields) into the shown copy. */
const mergeRow =
  (queryClient: QueryClient, co: string) =>
  (row: Partial<MessageView> & { id: string; thread_id: string }) =>
    updateMessage(queryClient, co, row.thread_id, row.id, (m) => {
      const { local: _sent, ...shown } = m;
      return { ...shown, ...row };
    });

/** Pin / unpin a message, at once. */
export function usePinMessage(chat: ChatContext) {
  const { api, co } = chat;
  const queryClient = useQueryClient();
  return useOptimisticMutation<MessageRef & { pinned: boolean }, MessageView>({
    keys: (v) => [messagesPrefix(co, v.threadId)],
    apply: (v) =>
      updateMessage(queryClient, co, v.threadId, v.id, (m) => ({
        ...m,
        pinned_at: v.pinned ? (m.pinned_at ?? new Date().toISOString()) : null,
      })),
    mutationFn: (v) =>
      api.client.messages.pin({
        params: { co, id: v.threadId, mid: v.id },
        body: { pinned: v.pinned },
      }) as Promise<MessageView>,
    reconcile: (row) => mergeRow(queryClient, co)(row),
  });
}

/** Edit a message's text, at once (inside the edit window; the server has the last word). */
export function useEditMessage(chat: ChatContext) {
  const { api, co } = chat;
  const queryClient = useQueryClient();
  return useOptimisticMutation<MessageRef & { markdown: string }, MessageView>({
    keys: (v) => [messagesPrefix(co, v.threadId)],
    apply: (v) => {
      const body = markdownToBlocks(v.markdown) as MessageView["body"];
      updateMessage(queryClient, co, v.threadId, v.id, (m) => ({
        ...m,
        body,
        body_text: bodyTextOf(body as Parameters<typeof bodyTextOf>[0]),
        edited_at: new Date().toISOString(),
      }));
    },
    mutationFn: (v) =>
      api.client.messages.update({
        params: { co, id: v.threadId, mid: v.id },
        body: { body_markdown: v.markdown },
      }) as Promise<MessageView>,
    reconcile: (row) => mergeRow(queryClient, co)(row),
  });
}

/** Delete a message (its tombstone shows at once). */
export function useDeleteMessage(chat: ChatContext) {
  const { api, co } = chat;
  const queryClient = useQueryClient();
  return useOptimisticMutation<MessageRef>({
    keys: (v) => [messagesPrefix(co, v.threadId)],
    apply: (v) =>
      updateMessage(queryClient, co, v.threadId, v.id, (m) => ({
        ...m,
        deleted_at: new Date().toISOString(),
        body: [] as unknown as MessageView["body"],
        body_text: "",
      })),
    mutationFn: (v) => api.client.messages.delete({ params: { co, id: v.threadId, mid: v.id } }),
  });
}

/**
 * Read marker: the topic's pill and its share of the channel's pill drop at once; the server
 * call follows (failures are quiet: the next refetch restores the true counts).
 */
export function markReadOptimistic(
  queryClient: QueryClient,
  co: string,
  channelId: string,
  topicId: string | null,
): void {
  let cleared = 0;
  queryClient.setQueryData<{ data: TopicSummary[] }>(chatKeys.topics(co, channelId), (d) => {
    if (!d) return d;
    return {
      ...d,
      data: d.data.map((t) => {
        if (topicId !== null && t.id !== topicId) return t;
        cleared += t.unread_count;
        return t.unread_count ? { ...t, unread_count: 0 } : t;
      }),
    };
  });
  queryClient.setQueryData<{ data: SidebarChannel[] }>(chatKeys.channels(co), (d) => {
    if (!d) return d;
    return {
      ...d,
      data: d.data.map((c) =>
        c.id === channelId && c.unread_count
          ? { ...c, unread_count: topicId === null ? 0 : Math.max(0, c.unread_count - cleared) }
          : c,
      ),
    };
  });
}
